import axios from "axios";
import Globais, { validateToken } from "../components/Globais";

// Tipos relacionados às entidades do backend
export interface CategoriaMensagem {
  id: number;
  nome: string;
}

export interface SeletorDestinatarios {
  id: number;
  nome: string;
}

export interface ModeloCampanha {
  id: number;
  nome: string;
  descricao?: string | null;
  prioridade?: number | null;
  frequenciaDias?: number | null;
  parametroSeletor?: Record<string, any> | null;
  seletorId?: number | null;
  categoriaMensagemId: number;
  ativo: boolean;
  SeletorDestinatarios?: { id: number; nome: string } | null;
  CategoriaMensagem?: { id: number; nome: string } | null;
  proximaExecucao?: string | null;
  limiteMensal?: number | null;
}


// Função auxiliar para configurar requisições autenticadas
const getHeaders = () => {
  const Token = validateToken();
  return Token ? { Authorization: Token.AuthStr } : {};
};

// ✅ Função auxiliar para normalizar resposta do backend
const normalizarModelo = (m: any): ModeloCampanha => ({
  ...m,
  ativo: m.active === 1, // converte number → boolean
});

// 🔹 Listar todos os modelos

const listarModeloCampanhasService = async (): Promise<ModeloCampanha[] | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas`;
    try {
      const response = await axios.get(URL, { headers: { Authorization: Token.AuthStr } });
      return response.data.map(normalizarModelo);
    } catch (error) {
      console.error("Erro ao listar modelos de campanhas:", error);
    }
  }
};

// 🔹 Listar apenas modelos ativos

const listarModelosCampanhasAtivasService = async (): Promise<ModeloCampanha[] | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas/ativos`;
    try {
      const response = await axios.get(URL, { headers: { Authorization: Token.AuthStr } });
      return response.data.map(normalizarModelo);
    } catch (error) {
      console.error("Erro ao listar modelos de campanhas ativas:", error);
    }
  }
};

// 🔹 Atualizar status (ativo/inativo)
const atualizarStatusModeloCampanhaService = async (
  id: number,
  ativo: boolean
): Promise<ModeloCampanha | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas/${id}`;
    try {
      // ✅ envia 'active' como número
      const response = await axios.put(
        URL,
        { active: ativo ? 1 : 0 },
        { headers: { Authorization: Token.AuthStr } }
      );
      return normalizarModelo(response.data);
    } catch (error) {
      console.error("Erro ao atualizar status de modelo de campanha:", error);
    }
  }
};

// 🔹 Excluir modelo
const excluirModeloCampanhaService = async (id: number): Promise<void> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas/${id}`;
    try {
      await axios.delete(URL, { headers: { Authorization: Token.AuthStr } });
    } catch (error) {
      console.error("Erro ao excluir modelo de campanha:", error);
    }
  }
};

// 🔹 Atualizar modelo
const atualizarModeloCampanhaService = async (
  id: number,
  dados: Partial<ModeloCampanha>
): Promise<ModeloCampanha | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas/${id}`;

    try {
      // 🔹 Monta payload de forma segura
      const payload: any = { ...dados };

      // Só envia o campo "active" se o valor "ativo" vier definido
      if (dados.ativo !== undefined) {
        payload.active = dados.ativo ? 1 : 0;
      }

      const response = await axios.put(URL, payload, {
        headers: { Authorization: Token.AuthStr },
      });

      return normalizarModelo(response.data);
    } catch (error) {
      console.error("Erro ao atualizar modelo de campanha:", error);
    }
  }
};


// 🔹 Criar modelo
const cadastrarModeloCampanhaService = async (
  dados: Partial<ModeloCampanha>
): Promise<ModeloCampanha | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas`;
    try {
      const response = await axios.post(
        URL,
        {
          ...dados,
          active: 1, // ✅ sempre cria ativo
        },
        { headers: { Authorization: Token.AuthStr } }
      );
      return normalizarModelo(response.data);
    } catch (error) {
      console.error("Erro ao cadastrar modelo de campanha:", error);
    }
  }
};

// 🔹 Buscar modelo de campanha por ID
const buscarModeloCampanhaPorIdService = async (
  id: number
): Promise<ModeloCampanha | undefined> => {
  const Token = validateToken();
  if (Token) {
    const URL = `${Globais.DOMINIOBACK}/modelocampanhas/${id}`;
    try {
      const response = await axios.get(URL, { headers: { Authorization: Token.AuthStr } });
      if (response && response.data) {
        return normalizarModelo(response.data);
      }
    } catch (error) {
      console.error("Erro ao buscar modelo de campanha por ID:", error);
    }
  }
};

// 🔹 Exporta todas as funções em um único objeto (boa prática)
export const modeloCampanhasService = {
  listarModeloCampanhasService,
  listarModelosCampanhasAtivasService,
  atualizarStatusModeloCampanhaService,
  excluirModeloCampanhaService,
  atualizarModeloCampanhaService,
  cadastrarModeloCampanhaService,
  buscarModeloCampanhaPorIdService
};
